import { useEffect, useRef } from 'react';
import { useSearch } from '../contexts/SearchContext';

interface SearchableData {
  [key: string]: any;
}

interface UsePageSearchOptions {
  data?: SearchableData[];
  searchFields?: string[];
  onSearch?: (term: string, filteredData?: SearchableData[]) => void;
  customFilter?: (data: SearchableData[], term: string) => SearchableData[];
}

export const usePageSearch = ({
  data = [],
  searchFields = [],
  onSearch,
  customFilter
}: UsePageSearchOptions = {}) => {
  const { searchTerm } = useSearch();
  const onSearchRef = useRef(onSearch);
  const dataRef = useRef(data);
  const searchFieldsRef = useRef(searchFields);
  const customFilterRef = useRef(customFilter);

  // Mettre à jour toutes les références
  useEffect(() => {
    onSearchRef.current = onSearch;
    dataRef.current = data;
    searchFieldsRef.current = searchFields;
    customFilterRef.current = customFilter;
  });

  useEffect(() => {
    // Debounce la recherche pour éviter les exécutions excessives
    const debounceTimer = setTimeout(() => {
      const handleSearch = (term: string) => {
        if (!term.trim()) {
          onSearchRef.current?.(term, dataRef.current);
          return;
        }

        let filteredData = dataRef.current;

        if (customFilterRef.current) {
          filteredData = customFilterRef.current(dataRef.current, term);
        } else {
          const searchLower = term.toLowerCase();
          filteredData = dataRef.current.filter(item => {
            // Si searchFields est spécifié, chercher seulement dans ces champs
            if (searchFieldsRef.current.length > 0) {
              return searchFieldsRef.current.some(field => {
                const fieldValue = getNestedValue(item, field);
                return String(fieldValue || '').toLowerCase().includes(searchLower);
              });
            }
            
            // Sinon, chercher dans toutes les propriétés de l'objet
            return searchInObject(item, searchLower);
          });
        }

        onSearchRef.current?.(term, filteredData);
      };

      // Exécuter la recherche avec le terme actuel
      handleSearch(searchTerm);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]); // Seulement searchTerm comme dépendance

  return {
    searchTerm,
    isSearching: !!searchTerm.trim()
  };
};

// Fonction utilitaire pour chercher dans un objet de manière récursive
const searchInObject = (obj: any, searchTerm: string): boolean => {
  if (obj === null || obj === undefined) {
    return false;
  }

  if (typeof obj === 'string' || typeof obj === 'number') {
    return String(obj).toLowerCase().includes(searchTerm);
  }

  if (Array.isArray(obj)) {
    return obj.some(item => searchInObject(item, searchTerm));
  }

  if (typeof obj === 'object') {
    return Object.values(obj).some(value => searchInObject(value, searchTerm));
  }

  return false;
};

// Fonction utilitaire pour obtenir une valeur imbriquée (ex: "user.profile.name")
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Hook pour la recherche simple dans du texte/contenu
export const useSimpleSearch = (onSearch: (term: string) => void) => {
  const { searchTerm } = useSearch();
  const onSearchRef = useRef(onSearch);

  // Mettre à jour la référence
  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  useEffect(() => {
    // Exécuter avec le terme actuel
    onSearchRef.current(searchTerm);
  }, [searchTerm]); // Seulement searchTerm comme dépendance

  return {
    searchTerm,
    isSearching: !!searchTerm.trim()
  };
};
